// Agent shape sent from Portal::AgentPresenter and consumed by
// pages/Agents/* and the AgentTable / SelectedAgentPanel components.
//
// Real Valet model is `Agent`; status / score / load fields are derived
// in a presenter — see plan domain mapping. Mock data conforms to this.
//
// Phase 4 (Iverson rewrite) added: rolePrompt, voiceAndTone, roleLabel,
// avatarUrl, active, aiModelId, humanEscalationEnabled, createdAt,
// rateLimits, config, rehearsalSummary. The Edit page binds these via
// useAutosave; AgentPresenter#as_edit emits the camelCase keys.

export type AgentStatus = "live" | "training" | "paused" | "draft"

export type RehearsalState =
  | "pending"
  | "running"
  | "passed"
  | "failed"
  | "errored"
  | "stale"

export type RehearsalSummary = {
  id: number
  state: string                 // server-emitted string; UI maps to chip tone
  passedCount: number
  total: number
  finishedAt: string | null
  targetSkillKey: string | null
}

export type Agent = {
  id: number
  uuid: string | null
  key: string
  name: string
  role: string                  // human-readable role label (legacy alias of roleLabel)
  description: string | null
  status: AgentStatus

  // Derived performance signals.
  resolutionRate: number        // 0..1
  qualityScore: number          // 0..100, average ai_user_rating-derived
  avgResponseTime: string       // formatted "0:38" / "1:12"
  ticketsHandled: number
  openTicketLoad: number

  // Skill links — id refs into Skill[].
  skillIds: number[]

  companyId: number
  updatedAt: string             // ISO timestamp

  // ── Phase 4 fields (Iverson rewrite) ───────────────────────────────────
  rolePrompt: string
  voiceAndTone: string | null
  roleLabel: string
  avatarUrl: string | null
  active: boolean
  aiModelId: number | null
  humanEscalationEnabled: boolean
  autoApproveEscalationCategories: boolean
  createdAt: string
  rateLimits: Record<string, unknown> | null
  greetings: string[]
  autoResolveAfterHours: number
  config: Record<string, unknown>
  rehearsalSummary: RehearsalSummary | null
}

// Defaults for the structured rate-limit form on Agents/Edit. Mirrors
// `Agents::RateLimiter::DEFAULTS` — keep in sync. UI shows these as
// placeholders so customers can see the inherited cap before overriding.
export const RATE_LIMIT_DEFAULTS = {
  messages_per_minute: 20,
  messages_per_hour: 200,
  convos_per_day: 50,
  daily_token_budget: 1_000_000,
} as const

export type RateLimitKey = keyof typeof RATE_LIMIT_DEFAULTS

// Lightweight catalog row for the Runtime config AI model dropdown.
export type AiModelOption = {
  id: number
  name: string
  provider?: string | null
}
